import { Icon } from '@chakra-ui/react'
import { Popover as ChakraPopover } from '@chakra-ui/react'
import Picker from '@emoji-mart/react'
import {
  File as FileIcon,
  FileText,
  Image as ImageIcon,
  Send,
  Smile,
  X,
} from 'lucide-react'
import { dropLast } from 'ramda'
import React, {
  ForwardedRef,
  RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import TextareaAutosize from 'react-textarea-autosize'
import styled from 'styled-components'
import { useUserState } from '~/atoms/UserState'
import CharacterCount from '~/components/CharacterCounter'
import { easeInOutTransition, focusedInput } from '~/components/CommonStyles'
import { Text } from '~/components/core'
import { FontStyle } from '~/components/core/Text'
import SnippetPopup from '~/components/SnippetPopup'
import { PopoverContent, PopoverTrigger } from '~/components/ui/popover'
import { Tooltip } from '~/components/ui/tooltip'
import { ImageTypes } from '~/constants/config'
import { dimensions, spacing } from '~/constants/theme'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useDebounce } from '~/hooks/useDebounce'
import useMaxCharacterCount from '~/hooks/useMaxCharacterCount'
import { useSearchSnippets } from '~/queries/snippetQueries'
import { desktop } from '~/styles'
import { AnalyticsNamespace, MessagingAnalyticsEvents } from '~/types/Analytics'
import { EmojiData } from '~/types/EmojiMartTypes'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { SettingsType } from '~/types/Settings'

import { MessageSnippet } from '@mangovoice/types'

// Without a `data` prop, emoji-mart fetches its dataset from cdn.jsdelivr.net at
// runtime, which the CSP blocks. Dynamic import so the ~430kB dataset stays out
// of the messaging chunk and only loads when the picker first mounts.
const loadEmojiData = () =>
  import('@emoji-mart/data').then((mod) => mod.default)

// emoji-mart reaches for the same CDN to translate its own chrome, so `i18n` has
// to be supplied too. Only the base language matters here; emoji-mart ships no
// Nepali or Telugu strings, so those fall back to English.
const emojiI18nLoaders: Record<string, () => Promise<{ default: unknown }>> = {
  en: () => import('@emoji-mart/data/i18n/en.json'),
  es: () => import('@emoji-mart/data/i18n/es.json'),
  fr: () => import('@emoji-mart/data/i18n/fr.json'),
  ko: () => import('@emoji-mart/data/i18n/ko.json'),
}

const loadEmojiI18n = (language?: string) => {
  const loader =
    emojiI18nLoaders[language?.split('-')[0] ?? ''] ?? emojiI18nLoaders.en
  return loader().then((mod) => mod.default)
}

const InputRootWrapper = styled.div`
  width: 100%;
`

// Reserves the gap above the composer in thread view; dropping it shifts the
// input up against the message list.
const InputTopSpacer = styled.div`
  width: 100%;
  height: 25px;
`

const InputFileWrapper = styled.div`
  width: 144px;
  height: 56px;
  padding: ${spacing.spacingLG};
  display: flex;
  align-items: center;
  border-radius: ${dimensions.borderRadiusSM};
  background-color: var(--chakra-colors-border-subtle);

  > p {
    margin-left: ${spacing.spacingLG};
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const InputImageRootWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`

const InputImageWrapper = styled.div`
  height: 66px;
  width: fit-content;
  padding-right: ${spacing.spacingLG};
  padding-top: ${spacing.spacingLG};
  margin-bottom: ${spacing.spacingLG};
  background-color: var(--chakra-colors-bg-panel);
  position: relative;
  ${easeInOutTransition}

  > img {
    border-radius: ${dimensions.borderRadiusSM};
  }

  > div {
    display: none;
    position: absolute;
    top: 0px;
    right: 0px;
    z-index: 10;
  }

  > ${InputFileWrapper} {
    display: flex;
    position: unset;
  }

  &:hover {
    > div {
      display: flex;
    }
  }
`

const InputImage = styled.img`
  height: 100%;
  width: auto;
`

interface InputWrapperProps {
  $opened?: boolean
  $hasExceededMaxCount?: boolean
}

const InputWrapper = styled.div<InputWrapperProps>`
  width: 100%;
  padding: 0 0 0 ${spacing.spacingLG};
  border-radius: ${dimensions.borderRadiusSM};

  border: 1px solid
    ${({ $hasExceededMaxCount }) =>
      $hasExceededMaxCount
        ? 'var(--chakra-colors-red-solid)'
        : 'var(--chakra-colors-fg-muted)'};
  background-color: var(--chakra-colors-bg-panel);
  ${(props) =>
    props.$opened
      ? 'border-top-right-radius: 0; border-top-left-radius: 0;'
      : ''}
  &:focus-within {
    ${focusedInput as any}
  }
  ${easeInOutTransition};
`

const InputFieldWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`

const InputBox = styled(TextareaAutosize)`
  width: 100%;
  padding: ${spacing.spacingMD} 0;
  font-size: 13px;
  line-height: 17px;
  border: none;
  resize: none;
  background-color: var(--chakra-colors-bg-panel);
  ${easeInOutTransition};

  &:focus {
    outline: none;
    outline-offset: 0;
    background-color: var(--chakra-colors-transparent);
  }

  &::-webkit-scrollbar {
    width: ${desktop.scrollbar};
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--chakra-colors-border-subtle);
    border-radius: ${desktop.scrollbar};
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
`

const ThemeAwareIconButton = styled.button<{ isActive?: boolean }>`
  width: 32px;
  height: 34px;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  color: ${({ isActive }) =>
    isActive
      ? 'var(--chakra-colors-brand-solid) !important'
      : 'var(--chakra-colors-fg-default) !important'};

  &:hover {
    color: ${({ isActive }) =>
      isActive
        ? 'var(--chakra-colors-brand-solid) !important'
        : 'var(--chakra-colors-fg-default-dark) !important'};
  }

  &:active {
    color: var(--chakra-colors-brand-solid) !important;
  }

  &:disabled {
    cursor: not-allowed;
    color: var(--chakra-colors-fg-muted) !important;
    opacity: 0.5;
  }

  & * {
    color: inherit !important;
  }
`

const SendIconButton = styled.button<{ disabled?: boolean }>`
  width: 32px;
  height: 34px;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

const CloseIconButton = styled.button`
  width: 20px;
  height: 20px;
  padding: 0;
  margin: 0;
  border: 2px solid var(--chakra-colors-border-subtle);
  border-radius: 50%;
  background: var(--chakra-colors-border-subtle);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--chakra-colors-fg-default);
  transition: all 0.2s ease;

  &:hover {
    background: var(--chakra-colors-border-default);
    color: var(--chakra-colors-fg-default);
  }

  &:active {
    background: var(--chakra-colors-border-default);
  }
`

export const stripSnippetTrigger = (text: string | undefined): string =>
  (text ?? '').replace(/#$/, '')

export const buildSnippetMessage = (
  precedingText: string,
  body: string,
): string => {
  const separator = precedingText && !/\s$/.test(precedingText) ? ' ' : ''
  return `${precedingText}${separator}${body}`
}

interface MessageInputProps {
  disabled?: boolean
  file?: File
  fileURL?: string | null
  opened?: boolean
  text?: string
  onChange?: (value: string | undefined) => void
  onKeyDown?: (e: KeyboardEvent) => void
  onOpen?: () => void
  onRemove?: () => void
  onSelect?: (e: EmojiData) => void
  onSend?: () => void
  isNewThread?: boolean
  snippetsDisabled?: boolean
  placeholder?: string
  /** Persistent accessible name for the composer; defaults to "Message". */
  ariaLabel?: string
}

const MessageInput = React.forwardRef(
  (props: MessageInputProps, ref: ForwardedRef<HTMLTextAreaElement>) => {
    const { t, i18n } = useTranslation()
    const emojiTriggerId = useId()
    const snippetListId = useId()

    const {
      disabled,
      file,
      fileURL,
      opened,
      text,
      onChange,
      onKeyDown,
      onOpen,
      onRemove,
      onSelect,
      onSend,
      isNewThread,
      snippetsDisabled,
      placeholder = t('messageInput.inputPlaceholder'),
      ariaLabel = t('a11y.composer.label'),
    } = props

    const forwardedRef = ref as React.RefObject<HTMLElement> | undefined | null
    const composerRef = useRef<HTMLTextAreaElement | null>(null)
    const setComposerRef = useCallback(
      (node: HTMLTextAreaElement | null) => {
        composerRef.current = node
        if (typeof ref === 'function') {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      },
      [ref],
    )

    const { pbxId } = useUserState()
    const { track } = useAnalytics(AnalyticsNamespace.MESSAGING)
    const [openSnippet, setOpenSnippet] = useState(false)
    const [position, setPosition] = useState(0)
    const [search, setSearch] = useState('')
    const [debounceSearch, setDebounceSearch] = useState('')
    const [oldText, setOldText] = useState('')
    const listRef = useRef<HTMLDivElement>(undefined)
    const settingsButtonRef = useRef<HTMLButtonElement>(null)
    const [snippets, setSnippets] = useState<MessageSnippet[]>([])
    const navigate = useNavigate()

    const { hasExceededMaxCount, count } = useMaxCharacterCount(
      text?.length ?? 0,
    )

    const handleSetOpenSnippet = (value: boolean) => {
      setOpenSnippet(value && !snippetsDisabled)
    }

    useSearchSnippets(
      search,
      (data) => {
        setSnippets(data.results.filter((s) => !s.pbx || s.pbx.id === pbxId))
        handleSetOpenSnippet(true)
      },
      {
        refetchOnWindowFocus: true,
        enabled: openSnippet,
      },
    )

    const snippetsLen = snippets.length
    const fileType = file?.type
    const fileName = file?.name

    const [, cancel] = useDebounce(
      () => {
        setSearch(debounceSearch)
      },
      500,
      [debounceSearch],
    )

    useEffect(() => {
      if (openSnippet) {
        setPosition(0)
        setOldText(stripSnippetTrigger(text))
      }
    }, [openSnippet])

    const focusItemAt = useCallback((index: number) => {
      const item = listRef.current?.children[index] as HTMLElement | undefined
      item?.focus()
    }, [])

    // A narrowing search can leave position past the end of the new results.
    useEffect(() => {
      if (position < snippets.length) return
      setPosition(0)
      focusItemAt(0)
    }, [snippets])

    useEffect(() => {
      if (text === oldText) {
        handleSetOpenSnippet(false)
      }
    }, [text, oldText])

    const handleChangeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e.target.value)
    }

    const handleSelectSnippet = useCallback(
      (snippet: MessageSnippet) => {
        const { id, body, name, scope } = snippet
        onChange?.(buildSnippetMessage(oldText, body))
        setDebounceSearch('')
        handleSetOpenSnippet(false)
        setTimeout(() => {
          forwardedRef?.current?.focus()
        }, 100)
        track(MessagingAnalyticsEvents.SnippetSelected, {
          id: id,
          name: name,
          scope: scope,
        })
      },
      [oldText, onChange, ref],
    )

    useEffect(() => {
      focusItemAt(0)
      cancel()
    }, [])

    const isSnippetPopupVisible =
      !snippetsDisabled && openSnippet && snippetsLen > 0

    // Dialogs listen for Escape on the document in the capture phase, so the
    // visible popup claims it earlier (window capture) to close only itself.
    useEffect(() => {
      if (!isSnippetPopupVisible) return
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key !== 'Escape') return
        const focusTarget = e.target instanceof Node ? e.target : null
        const isFocusInPopup =
          !!focusTarget &&
          (settingsButtonRef.current === focusTarget ||
            !!listRef.current?.contains(focusTarget))

        handleSetOpenSnippet(false)
        if (isFocusInPopup || focusTarget === composerRef.current) {
          e.stopPropagation()
        }
        if (isFocusInPopup) {
          composerRef.current?.focus()
        }
      }
      window.addEventListener('keydown', handleEscape, true)
      return () => window.removeEventListener('keydown', handleEscape, true)
    }, [isSnippetPopupVisible])

    useEffect(() => {
      const handleKeydown = (e: KeyboardEvent) => {
        if (openSnippet) {
          const focusTarget = e.target instanceof Node ? e.target : null

          if (e.key === 'Escape') {
            handleSetOpenSnippet(false)
            return
          }

          // Enter, Space and Tab keep their native behavior on the settings
          // button; ArrowDown returns to the list.
          if (focusTarget && settingsButtonRef.current === focusTarget) {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setPosition(0)
              focusItemAt(0)
            }
            return
          }

          switch (e.key) {
            case 'Enter':
            case 'Tab': {
              // Shift+Tab moves back to the settings button above the list.
              if (e.key === 'Tab' && e.shiftKey) break
              if (position < snippetsLen) {
                const selectedSnippet = snippets[position]

                e.preventDefault()
                handleSelectSnippet(selectedSnippet)
              }
              break
            }
            case 'ArrowDown': {
              if (position === snippets.length - 1 || snippets.length === 0)
                return
              const newPosition = position + 1
              setPosition(newPosition)
              focusItemAt(newPosition)
              break
            }
            case 'ArrowUp': {
              if (position === 0) {
                e.preventDefault()
                settingsButtonRef.current?.focus()
                return
              }
              const newPosition = position - 1
              setPosition(newPosition)
              focusItemAt(newPosition)
              break
            }
            case 'Backspace':
              if (debounceSearch !== '') {
                setDebounceSearch(dropLast(1, debounceSearch))
              }
              break
            default:
              onKeyDown?.(e)
          }

          return
        }
        onKeyDown?.(e)
      }

      addEventListener('keydown', handleKeydown)
      return () => removeEventListener('keydown', handleKeydown)
    }, [
      openSnippet,
      snippets,
      snippetsLen,
      debounceSearch,
      position,
      focusItemAt,
      handleSelectSnippet,
      onKeyDown,
    ])

    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (openSnippet) {
        setDebounceSearch(`${debounceSearch}${String.fromCharCode(e.charCode)}`)
      } else if (e.charCode === 35) {
        const last = text?.substring(text.length - 1, text.length)
        handleSetOpenSnippet(last !== '#')
      }
    }

    const handleSelectEmoji = (emoji: EmojiData) => {
      if (onSelect) onSelect(emoji)
    }

    const handleOpenSnippet = () => {
      if (snippetsDisabled) {
        return
      }

      if (!openSnippet) {
        const last = text?.substring(text.length - 1, text.length)
        if (!text || text.length === 0) {
          onChange?.('#')
        } else if (last !== '#') {
          onChange?.(`${text} #`)
        }
        setDebounceSearch('')

        if (ref) {
          forwardedRef?.current?.focus()
        }
      }
      handleSetOpenSnippet(!openSnippet)
    }

    const handleNavigation = () => {
      onChange?.(oldText)
      navigate(`${ApplicationRoutes.SETTINGS}/${SettingsType.SNIPPETS}`)
    }

    return (
      <InputRootWrapper>
        {!isNewThread ? <InputTopSpacer /> : null}

        <div style={{ position: 'relative' }}>
          {isSnippetPopupVisible && (
            <SnippetPopup
              listId={snippetListId}
              listRef={listRef as RefObject<HTMLDivElement>}
              settingsButtonRef={settingsButtonRef}
              data={snippets}
              position={position}
              onHover={(idx) => setPosition(idx)}
              onNavigate={handleNavigation}
              onSelect={(snippet) => handleSelectSnippet(snippet)}
              onShouldClose={() => handleSetOpenSnippet(false)}
            />
          )}
          <InputWrapper
            $hasExceededMaxCount={hasExceededMaxCount}
            $opened={openSnippet}
          >
            <InputFieldWrapper>
              <InputBox
                ref={setComposerRef}
                aria-label={ariaLabel}
                aria-controls={
                  isSnippetPopupVisible ? snippetListId : undefined
                }
                placeholder={placeholder}
                value={text}
                maxRows={5}
                minRows={isNewThread ? 4 : 1}
                cacheMeasurements
                onChange={handleChangeText}
                onKeyPress={handleKeyPress}
                autoFocus
              />
              {!fileURL && (
                <ButtonsWrapper>
                  <Tooltip
                    content={t('messageInput.attachMedia')}
                    positioning={{ placement: 'top' }}
                    contentProps={{
                      bg: 'var(--chakra-colors-bg-inverted)',
                      color: 'var(--chakra-colors-fg-inverted)',
                      fontSize: 'sm',
                      px: 3,
                      py: 2,
                      borderRadius: 'md',
                    }}
                  >
                    <ThemeAwareIconButton
                      aria-label={t('messageInput.attachMedia')}
                      isActive={opened}
                      onClick={onOpen}
                    >
                      <Icon as={ImageIcon} boxSize="22px" />
                    </ThemeAwareIconButton>
                  </Tooltip>
                  <Tooltip
                    content={t('messageInput.addSnippet')}
                    positioning={{ placement: 'top' }}
                    contentProps={{
                      bg: 'var(--chakra-colors-bg-inverted)',
                      color: 'var(--chakra-colors-fg-inverted)',
                      fontSize: 'sm',
                      px: 3,
                      py: 2,
                      borderRadius: 'md',
                    }}
                  >
                    <ThemeAwareIconButton
                      aria-label={t('messageInput.addSnippet')}
                      aria-haspopup="listbox"
                      aria-expanded={isSnippetPopupVisible}
                      aria-controls={
                        isSnippetPopupVisible ? snippetListId : undefined
                      }
                      isActive={openSnippet}
                      onClick={handleOpenSnippet}
                    >
                      <Icon as={FileText} boxSize="22px" />
                    </ThemeAwareIconButton>
                  </Tooltip>
                  <ChakraPopover.Root
                    lazyMount
                    ids={{ trigger: emojiTriggerId }}
                    positioning={{ placement: 'bottom-end' }}
                  >
                    <ChakraPopover.Context>
                      {(popover) => (
                        <>
                          <Tooltip
                            ids={{ trigger: emojiTriggerId }}
                            content={t('messageInput.emojis')}
                            positioning={{ placement: 'top' }}
                            contentProps={{
                              bg: 'var(--chakra-colors-bg-inverted)',
                              color: 'var(--chakra-colors-fg-inverted)',
                              fontSize: 'sm',
                              px: 3,
                              py: 2,
                              borderRadius: 'md',
                            }}
                          >
                            <PopoverTrigger asChild>
                              <ThemeAwareIconButton
                                aria-label={t('messageInput.emojis')}
                                isActive={popover.open}
                              >
                                <Icon as={Smile} boxSize="23px" />
                              </ThemeAwareIconButton>
                            </PopoverTrigger>
                          </Tooltip>
                          <PopoverContent>
                            <Picker
                              data={loadEmojiData}
                              i18n={() => loadEmojiI18n(i18n?.language)}
                              autoFocus={true}
                              emojiTooltip
                              theme={'light'}
                              sheetSize={32}
                              showPreview={false}
                              showSkinTones={false}
                              onEmojiSelect={handleSelectEmoji}
                            />
                          </PopoverContent>
                        </>
                      )}
                    </ChakraPopover.Context>
                  </ChakraPopover.Root>
                  {!isNewThread ? (
                    <Tooltip
                      content={t('messageInput.sendText')}
                      positioning={{ placement: 'top' }}
                      contentProps={{
                        bg: 'var(--chakra-colors-bg-inverted)',
                        color: 'var(--chakra-colors-fg-inverted)',
                        fontSize: 'sm',
                        px: 3,
                        py: 2,
                        borderRadius: 'md',
                      }}
                    >
                      <SendIconButton
                        aria-label={t('messageInput.sendText')}
                        disabled={disabled ?? hasExceededMaxCount}
                        onClick={onSend}
                      >
                        <Icon
                          as={Send}
                          boxSize="22px"
                          color={
                            (disabled ?? hasExceededMaxCount)
                              ? 'fg.muted'
                              : 'brand.solid'
                          }
                        />
                      </SendIconButton>
                    </Tooltip>
                  ) : null}
                </ButtonsWrapper>
              )}
            </InputFieldWrapper>
            {fileURL && (
              <InputImageRootWrapper>
                <InputImageWrapper>
                  {ImageTypes.includes(fileType ?? '') ? (
                    <InputImage src={fileURL} />
                  ) : (
                    <InputFileWrapper>
                      <FileIcon
                        size={30}
                        color="var(--chakra-colors-fg-muted)"
                      />
                      <Text
                        fontStyle={FontStyle.ExTiny}
                        text={fileName}
                        color="var(--chakra-colors-fg-default)"
                      />
                    </InputFileWrapper>
                  )}

                  <CloseIconButton onClick={onRemove}>
                    <Icon boxSize="8px">
                      <X size="100%" />
                    </Icon>
                  </CloseIconButton>
                </InputImageWrapper>
                {!isNewThread ? (
                  <Tooltip
                    content={t('messageInput.sendText')}
                    positioning={{ placement: 'top' }}
                    contentProps={{
                      bg: 'var(--chakra-colors-bg-inverted)',
                      color: 'var(--chakra-colors-fg-inverted)',
                      fontSize: 'sm',
                      px: 3,
                      py: 2,
                      borderRadius: 'md',
                    }}
                  >
                    <SendIconButton
                      aria-label={t('messageInput.sendText')}
                      disabled={disabled}
                      onClick={onSend}
                    >
                      <Icon
                        as={Send}
                        boxSize="22px"
                        color={disabled ? 'fg.muted' : 'brand.solid'}
                      />
                    </SendIconButton>
                  </Tooltip>
                ) : null}
              </InputImageRootWrapper>
            )}
          </InputWrapper>
          <CharacterCount count={count} />
        </div>
      </InputRootWrapper>
    )
  },
)

export default MessageInput
